import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import InstagramHome from './Settings/InstagramHome';
import SocialInboxView from './Settings/SocialInboxView';
import SocialAccountsView from './Settings/SocialAccountsView';
import { useAuth } from './Auth/AuthContext';
import { listSocialConnections, startSocialConnect, disconnectSocial } from '../utils/socialApi';
import { toast } from './Toast';

export default function SocialPage() {
    const { section = 'calendar' } = useParams();
    const navigate = useNavigate();
    const { tokens } = useAuth();

    const [igConnected, setIgConnected] = useState(false);
    const [igHandle, setIgHandle] = useState('');
    const [igDisplayName, setIgDisplayName] = useState('');
    const [igAvatarUrl, setIgAvatarUrl] = useState('');
    const [igTimezone, setIgTimezone] = useState('');
    const [igAutoPublish, setIgAutoPublish] = useState(true);
    const [igBusy, setIgBusy] = useState(false);
    const [igLoading, setIgLoading] = useState(true);

    // Fetch Instagram connection status
    const fetchStatus = useCallback(() => {
        if (!tokens?.accessToken) {
            setIgLoading(false);
            return;
        }
        setIgLoading(true);
        listSocialConnections({ accessToken: tokens.accessToken })
            .then((res) => {
                const connections = res.ok ? (res.data?.connections || []) : [];
                const ig = connections.find((c) => c.platform === 'instagram');
                const igProper = !!(ig?.handle || ig?.displayName || ig?.profileUrl);
                setIgConnected(igProper);
                setIgHandle(ig?.handle || '');
                setIgDisplayName(ig?.displayName || '');
                setIgAvatarUrl(ig?.avatarUrl || '');
                setIgTimezone(ig?.timezone || '');
                setIgAutoPublish(ig?.autoPublish ?? true);
            })
            .catch(() => {
                setIgConnected(false);
                setIgHandle('');
                setIgDisplayName('');
                setIgAvatarUrl('');
                setIgTimezone('');
                setIgAutoPublish(true);
            })
            .finally(() => setIgLoading(false));
    }, [tokens?.accessToken]);

    useEffect(() => { fetchStatus(); }, [fetchStatus]);

    // Listen for popup result
    useEffect(() => {
        const onMessage = (e) => {
            if (e.origin !== window.location.origin) return;
            if (e.data?.type !== 'social-auth-result') return;
            setIgBusy(false);
            if (e.data.ok) {
                toast.success(e.data.handle ? `Instagram connected as @${e.data.handle}` : 'Instagram connected');
                setIgHandle(e.data.handle || '');
                fetchStatus(); // fetchStatus will set igConnected correctly via isProperlyConnected check
            } else {
                toast.error(e.data.error || 'Instagram connection failed');
            }
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [fetchStatus]);

    // Start Instagram connect — open popup window
    const handleConnect = async () => {
        if (!tokens?.accessToken) return;
        setIgBusy(true);
        const redirectUrl = `${window.location.origin}/app/auth/social/callback`;
        const res = await startSocialConnect({ platform: 'instagram', redirectUrl, accessToken: tokens.accessToken });
        if (res.ok && res.data?.redirectUrl) {
            const w = 500, h = 650;
            const left = window.screenX + (window.outerWidth - w) / 2;
            const top = window.screenY + (window.outerHeight - h) / 2;
            const popup = window.open(res.data.redirectUrl, 'instagram-connect', `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`);
            // Check if popup was blocked or closed
            const check = setInterval(() => {
                if (!popup || popup.closed) {
                    clearInterval(check);
                    setIgBusy(false);
                }
            }, 500);
        } else {
            setIgBusy(false);
            toast.error(res.error || 'Failed to start Instagram connection');
        }
    };

    // Disconnect Instagram
    const handleDisconnect = async () => {
        if (!tokens?.accessToken) return;
        setIgBusy(true);
        const res = await disconnectSocial({ platform: 'instagram', accessToken: tokens.accessToken });
        setIgBusy(false);
        if (res.ok) {
            setIgConnected(false);
            setIgHandle('');
            setIgDisplayName('');
            setIgAvatarUrl('');
            setIgTimezone('');
            setIgAutoPublish(true);
            toast.success('Instagram disconnected');
            window.dispatchEvent(new CustomEvent('social:connections-changed'));
            fetchStatus();
        } else {
            toast.error(res.error || 'Failed to disconnect Instagram');
        }
    };

    // Optimistic update from the SocialAccountsView save call
    const handlePreferencesSaved = ({ timezone, autoPublish }) => {
        if (timezone !== undefined) setIgTimezone(timezone || '');
        if (autoPublish !== undefined) setIgAutoPublish(autoPublish);
    };

    if (section === 'inbox') return <SocialInboxView instagramConnected={igConnected} onInstagramConnect={handleConnect} instagramBusy={igBusy} accessToken={tokens?.accessToken} />;
    if (section === 'accounts') return (
        <SocialAccountsView
            instagramConnected={igConnected}
            instagramHandle={igHandle}
            instagramDisplayName={igDisplayName}
            instagramAvatarUrl={igAvatarUrl}
            instagramTimezone={igTimezone}
            instagramAutoPublish={igAutoPublish}
            instagramBusy={igBusy}
            onInstagramConnect={handleConnect}
            onInstagramDisconnect={handleDisconnect}
            onPreferencesSaved={handlePreferencesSaved}
        />
    );
    return (
        <InstagramHome
            instagramConnected={igConnected}
            instagramHandle={igHandle}
            instagramDisplayName={igDisplayName}
            instagramAvatarUrl={igAvatarUrl}
            onInstagramConnect={handleConnect}
            instagramBusy={igBusy}
            instagramLoading={igLoading}
        />
    );
}
